import { View, DataView, TableView, ContextMenu, Dialog, Modal, Collection, Model } from 'web-mojo';
import { Machine } from '@portal/models/Machine.js';
import { Device } from '@portal/models/Device.js';
import DeviceFirmwareView from '../firmware/views/DeviceFirmwareView.js';

class MachineHistoryList extends Collection {
    constructor(opts = {}) {
        super({ endpoint: '/api/camaccess/machine/history', ...opts });
    }
}

class TerminalView extends View {
    constructor(options = {}) {
        super({
            className: 'terminal-view',
            ...options
        });
    }

    async getTemplate() {
        return `
        <style>
            .tv { font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif; -webkit-font-smoothing: antialiased; }
            .tv-hd { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.125rem 0.75rem; border-bottom: 1px solid rgba(0,0,0,0.05); }
            .tv-hd-left { display: flex; align-items: center; gap: 0.625rem; }
            .tv-name { font-size: 0.9rem; font-weight: 600; color: #27272a; }
            .tv-location { font-size: 0.72rem; color: #a1a1aa; }
            .tv-dot-on { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; flex-shrink: 0; box-shadow: 0 0 6px rgba(34,197,94,0.45); animation: tv-pulse 2.5s ease infinite; }
            .tv-dot-off { width: 7px; height: 7px; border-radius: 50%; background: #d4d4d8; flex-shrink: 0; }
            @keyframes tv-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
            .tv-hero { padding: 1.25rem 1.125rem; position: relative; }
            .tv-state { font-size: 2rem; font-weight: 800; letter-spacing: -0.04em; line-height: 1; margin-bottom: 0.25rem; }
            .tv-sub { font-size: 0.72rem; color: #a1a1aa; margin-bottom: 0.75rem; }
            .tv-pips { display: flex; gap: 0.35rem; flex-wrap: wrap; }
            .tv-pip { display: flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; font-size: 0.62rem; font-weight: 600; border: 1px solid transparent; }
            .tv-pip .pd { width: 5px; height: 5px; border-radius: 50%; }
            .tv-pip.pip-success { background: rgba(22,163,74,0.08); color: #15803d; border-color: rgba(22,163,74,0.15); }
            .tv-pip.pip-success .pd { background: #22c55e; }
            .tv-pip.pip-warning { background: rgba(217,119,6,0.08); color: #92400e; border-color: rgba(217,119,6,0.15); }
            .tv-pip.pip-warning .pd { background: #f59e0b; }
            .tv-pip.pip-danger { background: rgba(220,38,38,0.08); color: #b91c1c; border-color: rgba(220,38,38,0.15); }
            .tv-pip.pip-danger .pd { background: #ef4444; }
            .tv-pip.pip-muted { background: rgba(0,0,0,0.04); color: #71717a; border-color: rgba(0,0,0,0.07); }
            .tv-pip.pip-muted .pd { background: #a1a1aa; }
            .tv .table { --bs-table-bg: transparent; }
            .tv-tabs { display: flex; gap: 0.25rem; padding: 0.625rem 1.125rem; border-top: 1px solid rgba(0,0,0,0.05); border-bottom: 1px solid rgba(0,0,0,0.05); }
            .tv-tab { flex: 1; padding: 0.45rem 0; border: none; background: none; cursor: pointer; font-size: 0.72rem; font-weight: 600; color: #a1a1aa; border-radius: 8px; transition: background 0.2s, color 0.2s; }
            .tv-tab:hover { color: #71717a; background: rgba(0,0,0,0.03); }
            .tv-tab.active { color: #0d6efd; background: rgba(13,110,253,0.09); }
            .tv-panel { display: none; }
            .tv-panel.active { display: block; }
            .tv-cmd-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; padding: 1rem 1.125rem; }
            .tv-cmd-btn { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.75rem; border: 1px solid rgba(0,0,0,0.1); border-radius: 8px; background: #fff; cursor: pointer; font-size: 0.75rem; font-weight: 500; color: #3f3f46; transition: background 0.15s, border-color 0.15s; }
            .tv-cmd-btn:hover { background: #f4f4f5; border-color: rgba(0,0,0,0.2); }
            .tv-cmd-btn i { font-size: 0.9rem; color: #71717a; }
            .tv-cmd-btn.cmd-danger { color: #b91c1c; }
            .tv-cmd-btn.cmd-danger i { color: #dc2626; }
            .tv-cmd-section { padding: 0.75rem 1.125rem 0; }
            .tv-cmd-label { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #a1a1aa; margin-bottom: 0.5rem; }
            .tv-serial-log { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 0.68rem; background: #18181b; color: #a1a1aa; border-radius: 8px; padding: 0.75rem; min-height: 80px; max-height: 200px; overflow-y: auto; white-space: pre-wrap; word-break: break-all; }
        </style>

        <div class="tv">
            <div class="tv-hd">
                <div class="tv-hd-left">
                    <span data-bind="online-dot"></span>
                    <div>
                        <div class="tv-name" data-bind="terminal-name"></div>
                        <div class="tv-location" data-bind="terminal-location"></div>
                    </div>
                </div>
                <div data-container="view-context-menu"></div>
            </div>

            <div class="tv-hero">
                <div class="tv-state" data-bind="state-word"></div>
                <div class="tv-sub" data-bind="state-sub"></div>
                <div class="tv-pips" data-bind="pips"></div>
            </div>

            <div class="tv-tabs">
                <button class="tv-tab active" data-tab="details">Details</button>
                <button class="tv-tab" data-tab="commands">Commands</button>
                <button class="tv-tab" data-tab="activity">Activity</button>
                {{#showFirmwareTab}}<button class="tv-tab" data-tab="firmware">Firmware</button>{{/showFirmwareTab}}
            </div>

            <div class="tv-panel active" data-panel="details">
                <div data-container="details-view"></div>
                <div data-container="qr-view"></div>
            </div>

            <div class="tv-panel" data-panel="commands">
                <div class="tv-cmd-section">
                    <div class="tv-cmd-label">Admin Commands</div>
                </div>
                <div class="tv-cmd-grid">
                    <button type="button" class="tv-cmd-btn" data-action="cmd-get-status">
                        <i class="bi bi-info-circle"></i> Get Status
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-reload">
                        <i class="bi bi-arrow-clockwise"></i> Reload UI
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-send-toast">
                        <i class="bi bi-chat-square-text"></i> Send Toast
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-set-mode">
                        <i class="bi bi-toggles"></i> Set Mode
                    </button>
                    <button type="button" class="tv-cmd-btn cmd-danger" data-action="cmd-restart">
                        <i class="bi bi-power"></i> Restart App
                    </button>
                </div>

                <div class="tv-cmd-section">
                    <div class="tv-cmd-label">Device Commands</div>
                </div>
                <div class="tv-cmd-grid">
                    <button type="button" class="tv-cmd-btn" data-action="cmd-device" data-cmd="LOCK_STATUS">
                        <i class="bi bi-lock"></i> Lock Status
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-device" data-cmd="LOCK_ENGAGE">
                        <i class="bi bi-lock-fill"></i> Lock Engage
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-device" data-cmd="LOCK_RELEASE">
                        <i class="bi bi-unlock"></i> Lock Release
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-device" data-cmd="SYS_INFO">
                        <i class="bi bi-cpu"></i> Sys Info
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-device" data-cmd="BLE_STATUS">
                        <i class="bi bi-bluetooth"></i> BLE Status
                    </button>
                    <button type="button" class="tv-cmd-btn" data-action="cmd-device" data-cmd="SENSOR_READ">
                        <i class="bi bi-thermometer-half"></i> Sensor Read
                    </button>
                </div>

                <div class="tv-cmd-section">
                    <div class="tv-cmd-label">Serial Console</div>
                </div>
                <div class="px-3 pb-3">
                    <div class="input-group input-group-sm mb-2">
                        <input type="text" class="form-control font-monospace" name="serial_command"
                            placeholder="Raw serial command...">
                        <button type="button" class="btn btn-outline-primary" data-action="cmd-run-serial">Run</button>
                    </div>
                    <div class="tv-serial-log" data-bind="serial-log">Waiting for command...</div>
                </div>
            </div>

            <div class="tv-panel" data-panel="activity">
                <div data-container="activity-view"></div>
            </div>

            {{#showFirmwareTab}}
            <div class="tv-panel" data-panel="firmware">
                {{^firmwareUnavailable}}<div data-container="firmware-view"></div>{{/firmwareUnavailable}}
                {{#firmwareUnavailable}}<div class="p-3 text-muted small">Firmware controls unavailable — the paired lock device could not be loaded.</div>{{/firmwareUnavailable}}
            </div>
            {{/showFirmwareTab}}
        </div>
        `;
    }

    async onInit() {
        const id = this.model.get('id');
        if (id) {
            const response = await app.api.get(`/api/camaccess/machine/${id}`);
            if (response.success && response.data.status) {
                this.model.set(response.data.data);
            }
        }

        const state = this.model.get('activation_state') || 'pending';

        // Details — DataView
        this.detailsView = new DataView({
            model: this.model,
            containerId: 'details-view',
            className: 'p-3',
            showEmptyValues: true,
            fields: [
                { name: 'name', label: 'Name', colSize: 6 },
                { name: 'location.name', label: 'Location', colSize: 6 },
                { name: 'machine_type', label: 'Machine Type', colSize: 6 },
                { name: 'serial_number', label: 'Serial Number', colSize: 6 },
                { name: 'uuid', label: 'UUID', colSize: 12 },
                { name: 'device.name', label: 'Lock Device', colSize: 6 },
                { name: 'last_seen', label: 'Last Seen', formatter: 'relative', colSize: 6 },
                { name: 'created', label: 'Added', formatter: 'datetime', colSize: 6 },
            ]
        });
        this.addChild(this.detailsView);

        // QR Code section — Mustache template
        const qrId = this.model.get('qr_code_id');
        this.qrView = new View({
            containerId: 'qr-view',
            template: `
            <div class="px-3 pb-3">
                <div class="fw-semibold small text-uppercase text-muted mb-2">QR Code</div>
                <label class="form-label small">QR Code ID</label>
                <div class="input-group input-group-sm" style="max-width:360px">
                    <input type="text" class="form-control font-monospace" name="qr_code_id"
                        value="{{model.qr_code_id}}" placeholder="e.g. lobby-snack-1">
                    <button type="button" class="btn btn-outline-secondary" data-action="save-qr">Save</button>
                </div>
                {{#model.qr_code_id}}
                <div class="form-text">Consumer URL: <span class="font-monospace">{{consumerUrl}}</span></div>
                {{/model.qr_code_id}}
                {{^model.qr_code_id}}
                <div class="form-text text-muted">Set a QR code ID to generate the consumer URL</div>
                {{/model.qr_code_id}}
            </div>`
        });
        this.qrView.consumerUrl = qrId ? `${window.location.origin}/vend/${qrId}/` : '';
        this.qrView.setModel(this.model);
        this.addChild(this.qrView);

        // Activity — TableView with pagination
        this.activityView = new TableView({
            containerId: 'activity-view',
            collection: new MachineHistoryList({
                params: { machine: id, sort: '-created', size: 10 }
            }),
            fetchOnMount: true,
            hideActivePillNames: ['machine'],
            clickAction: 'none',
            fontSize: 'sm',
            paginated: true,
            columns: [
                { key: 'created', label: 'Time', formatter: 'relative', sortable: true },
                { key: 'action|badge:activate=success,suspend=warning,revoke=danger,unlock=primary,ws_connected=success,ws_disconnected=secondary,error=danger', label: 'Event' },
                { key: 'source|badge', label: 'Source' },
                { key: 'note', label: 'Note', formatter: "default('—')" },
            ]
        });
        this.addChild(this.activityView);

        // Firmware — reuse DeviceFirmwareView for the paired lock device.
        // Gated on a device being paired AND the operator holding an
        // (unprefixed, so member-tier) manage_firmware grant AND a cam-device
        // view perm: the reused view reads GET /api/camaccess/device/<id>,
        // which manage_firmware alone does not grant, and TerminalsPage also
        // admits payments-perm users — so both perms must be present or the
        // tab would render dead. Same two-tier authority as ota/trigger.
        const fwApp = this.getApp();
        const canFirmware = !!fwApp.activeUser?.hasPermission('manage_firmware');
        const canViewDevice = !!(fwApp.activeUser?.hasPermission('view_cam_devices')
            || fwApp.activeUser?.hasPermission('manage_cam_devices'));
        const paired = this.model.get('device');
        this.showFirmwareTab = !!paired && canFirmware && canViewDevice;
        this.firmwareUnavailable = false;
        if (this.showFirmwareTab) {
            // Fetch the full Device (the machine's device graph is basic — the
            // reused view needs metadata.version, variant identity, group.id).
            // A load failure degrades to a muted note, never a thrown init.
            const deviceModel = new Device({ id: paired.id });
            try {
                await deviceModel.fetch();
                if (deviceModel.get('uuid')) {
                    this.firmwareView = new DeviceFirmwareView({
                        model: deviceModel,
                        containerId: 'firmware-view',
                    });
                    this.addChild(this.firmwareView);
                } else {
                    this.firmwareUnavailable = true;
                }
            } catch {
                this.firmwareUnavailable = true;
            }
        }

        // Context menu — all actions live here
        const menuItems = [];
        if (this.model.get('device')) {
            menuItems.push({ label: 'Admin Unlock', action: 'admin-unlock', icon: 'bi-unlock' });
            menuItems.push({ type: 'divider' });
        }
        if (state === 'pending' || state === 'suspended') {
            menuItems.push({ label: 'Activate', action: 'activate', icon: 'bi-check-circle' });
        }
        if (state === 'active') {
            menuItems.push({ label: 'Suspend', action: 'suspend', icon: 'bi-pause-circle' });
        }
        menuItems.push({ type: 'divider' });
        menuItems.push({ label: 'Edit', action: 'edit', icon: 'bi-pencil' });
        if (state !== 'revoked') {
            menuItems.push({ type: 'divider' });
            menuItems.push({ label: 'Revoke', action: 'revoke', icon: 'bi-x-circle', className: 'text-danger' });
        }

        const contextMenu = new ContextMenu({
            containerId: 'view-context-menu',
            className: 'context-menu-view header-menu-absolute',
            context: this.model,
            config: {
                icon: 'bi-three-dots',
                items: menuItems
            }
        });
        this.addChild(contextMenu);

        // WebSocket listener for device status
        this._onDeviceStatus = (data) => {
            const dev = this.model.get('device');
            if (dev && dev.id === data?.device_id) {
                dev.is_online = data.is_online;
                dev.lock_engaged = data.lock_engaged;
                this.model.set('device', dev);
                this._updateHeroUI();
            }
        };
        app.events.on('message:lock', this._onDeviceStatus);
    }

    async onAfterRender() {
        this._updateHeroUI();
        this._bindTabs();
    }

    _bindTabs() {
        const tabs = this.el?.querySelectorAll('.tv-tab');
        if (!tabs) return;
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                tabs.forEach(t => t.classList.remove('active'));
                tab.classList.add('active');
                this.el.querySelectorAll('.tv-panel').forEach(p => p.classList.remove('active'));
                const panel = this.el.querySelector(`[data-panel="${tab.dataset.tab}"]`);
                if (panel) panel.classList.add('active');
            });
        });
    }

    _updateHeroUI() {
        const state = this.model.get('activation_state') || 'pending';
        const dev = this.model.get('device');
        const online = dev?.is_online;

        const dotEl = this._el('online-dot');
        if (dotEl) dotEl.className = online ? 'tv-dot-on' : 'tv-dot-off';

        this._setText('terminal-name', this.model.get('name') || 'Terminal');
        this._setText('terminal-location', this.model.get('location')?.name || 'Unassigned');

        const stateMap = { active: 'Active', pending: 'Pending', suspended: 'Suspended', revoked: 'Revoked' };
        const stateEl = this._el('state-word');
        if (stateEl) {
            stateEl.textContent = stateMap[state] || state;
            const colors = { active: '#16a34a', pending: '#d97706', suspended: '#71717a', revoked: '#dc2626' };
            stateEl.style.color = colors[state] || '#71717a';
        }

        const subMap = {
            active: dev ? (online ? 'Terminal online' : 'Device offline') : 'No lock assigned',
            pending: 'Awaiting activation',
            suspended: 'Terminal suspended',
            revoked: 'Terminal permanently revoked',
        };
        this._setText('state-sub', subMap[state] || '');

        // Pips — real-time IoT state indicators (same pattern as DeviceView)
        let pips = '';
        const pipClass = { active: 'pip-success', pending: 'pip-warning', suspended: 'pip-muted', revoked: 'pip-danger' };
        pips += `<span class="tv-pip ${pipClass[state] || 'pip-muted'}"><span class="pd"></span> ${stateMap[state] || state}</span>`;
        if (dev) {
            pips += `<span class="tv-pip ${online ? 'pip-success' : 'pip-muted'}"><span class="pd"></span> ${online ? 'Online' : 'Offline'}</span>`;
            if (dev.lock_engaged !== undefined) {
                pips += `<span class="tv-pip ${dev.lock_engaged ? 'pip-danger' : 'pip-success'}"><span class="pd"></span> ${dev.lock_engaged ? 'Locked' : 'Unlocked'}</span>`;
            }
        } else {
            pips += `<span class="tv-pip pip-muted"><span class="pd"></span> No Lock</span>`;
        }
        this._setHTML('pips', pips);
    }

    // ── Actions (all via ContextMenu) ────────────────────────────────────────

    async onActionActivate() {
        const response = await this.model.save({ activate: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to activate');
            return;
        }
        app.toast.success('Terminal activated');
        await this.model.fetch();
        this._updateHeroUI();
    }

    async onActionSuspend() {
        const confirmed = await Dialog.confirm({
            title: 'Suspend Terminal',
            message: `Suspend "${this.model.get('name') || 'this terminal'}"? It can be reactivated later.`,
            confirmText: 'Suspend',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;
        const response = await this.model.save({ suspend: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to suspend');
            return;
        }
        app.toast.success('Terminal suspended');
        await this.model.fetch();
        this._updateHeroUI();
    }

    async onActionRevoke() {
        const confirmed = await Dialog.confirm({
            title: 'Revoke Terminal',
            message: `Permanently revoke "${this.model.get('name') || 'this terminal'}"? This cannot be undone.`,
            confirmText: 'Revoke',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        const response = await this.model.save({ revoke: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to revoke');
            return;
        }
        app.toast.success('Terminal revoked');
        await this.model.fetch();
        this._updateHeroUI();
    }

    async onActionAdminUnlock() {
        if (!this.model.get('device')) {
            app.toast.error('No lock device assigned');
            return;
        }
        const confirmed = await Dialog.confirm({
            title: 'Admin Unlock',
            message: `Send an unlock command to "${this.model.get('name') || 'this terminal'}"?`,
            confirmText: 'Unlock',
            confirmClass: 'btn-primary',
        });
        if (!confirmed) return;
        const response = await this.model.save({
            unlock: { reason: 'portal admin unlock', ttl: 30 },
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to send unlock');
            return;
        }
        app.toast.success('Unlock dispatched');
    }

    async onActionEdit() {
        Dialog.showModelForm({
            model: this.model,
            title: `Edit Terminal — ${this.model.get('name') || ''}`,
            fields: Machine.EDIT_FORM.fields,
        });
    }

    async onActionSaveQr() {
        const qrId = this.el?.querySelector('[name=qr_code_id]')?.value?.trim();
        if (!qrId) {
            app.toast.warning('Enter a QR code ID');
            return;
        }
        const existing = this.model.get('qr_code_id');
        if (existing && existing !== qrId) {
            const confirmed = await Dialog.confirm({
                title: 'Change QR Code',
                message: 'Changing the QR code will invalidate the old one. Continue?',
                confirmText: 'Change',
                confirmClass: 'btn-warning',
            });
            if (!confirmed) return;
        }
        const response = await this.model.save({ qr_code_id: qrId });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to save QR code');
            return;
        }
        app.toast.success('QR code ID saved');
        this.qrView.consumerUrl = `${window.location.origin}/vend/${qrId}/`;
        this.qrView.render();
    }

    // ── Remote Commands ───────────────────────────────────────────────────────

    async _sendCommand(action, data, successMsg) {
        const response = await this.model.save({ [action]: data });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || `Failed: ${action}`);
            return null;
        }
        if (successMsg) app.toast.success(successMsg);
        return response.data.data;
    }

    async onActionCmdReload() {
        await this._sendCommand('admin_reload', true, 'Reload sent');
    }

    async onActionCmdRestart() {
        const confirmed = await Dialog.confirm({
            title: 'Restart App',
            message: `Restart the app on "${this.model.get('name') || 'this terminal'}"? The terminal will reconnect automatically.`,
            confirmText: 'Restart',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        await this._sendCommand('admin_restart', { mode: 'app' }, 'Restart sent');
    }

    async onActionCmdGetStatus() {
        app.toast.info('Requesting status...');
        const result = await this._sendCommand('get_status', true);
        if (!result) return;
        if (result.status === 'offline') {
            app.toast.warning('Terminal is offline');
            return;
        }
        if (result.status === 'timeout') {
            app.toast.warning('Terminal did not respond');
            return;
        }
        const info = result.data || result;
        const lines = [];
        if (info.app) lines.push(`App: v${info.app.version_name} (${info.app.version_code})`);
        if (info.device) lines.push(`Device: ${info.device.manufacturer} ${info.device.model}, Android ${info.device.os_version}`);
        if (info.battery) lines.push(`Battery: ${info.battery.level}%${info.battery.is_charging ? ' (charging)' : ''}`);
        if (info.storage) lines.push(`Storage: ${info.storage.available_mb}MB / ${info.storage.total_mb}MB`);
        if (info.memory) lines.push(`Memory: ${info.memory.available_mb}MB / ${info.memory.total_mb}MB${info.memory.low_memory ? ' LOW' : ''}`);
        if (info.uptime) lines.push(`Uptime: device ${Math.round(info.uptime.device_seconds / 3600)}h, app ${Math.round(info.uptime.process_seconds / 60)}m`);
        if (info.app_state) lines.push(`Screen: ${info.app_state.current_screen || '—'}, State: ${info.app_state.activation_state || '—'}`);

        await Modal.alert({
            title: 'Terminal Status',
            body: `<pre class="mb-0 small">${lines.join('\n')}</pre>`,
            size: 'md',
        });
    }

    async onActionCmdSendToast() {
        const result = await Modal.form({
            title: 'Send Toast to Terminal',
            fields: [
                { name: 'text', type: 'text', label: 'Message', required: true },
                { name: 'level', type: 'select', label: 'Level', columns: 6,
                  options: [
                      { label: 'Info', value: 'info' },
                      { label: 'Warning', value: 'warn' },
                      { label: 'Error', value: 'error' },
                  ]},
                { name: 'duration_ms', type: 'number', label: 'Duration (ms)', columns: 6, value: 5000 },
            ],
        });
        if (!result) return;
        await this._sendCommand('send_toast', result, 'Toast sent');
    }

    async onActionCmdSetMode() {
        const result = await Modal.form({
            title: 'Set Terminal Mode',
            fields: [
                { name: 'mode', type: 'select', label: 'Mode', required: true,
                  options: [
                      { label: 'Operational', value: 'operational' },
                      { label: 'Service', value: 'service' },
                      { label: 'Maintenance', value: 'maintenance' },
                      { label: 'Custom', value: 'custom' },
                  ]},
                { name: 'message', type: 'text', label: 'Message (for custom/service/maintenance)' },
            ],
        });
        if (!result) return;
        await this._sendCommand('set_mode', result, `Mode set to ${result.mode}`);
    }

    async onActionCmdDevice(event, el) {
        const command = el.getAttribute('data-cmd');
        if (!command) return;
        await this._runSerial(command);
    }

    async onActionCmdRunSerial() {
        const input = this.el?.querySelector('[name=serial_command]');
        const command = input?.value?.trim();
        if (!command) {
            app.toast.warning('Enter a serial command');
            return;
        }
        await this._runSerial(command);
        input.value = '';
    }

    async _runSerial(command) {
        const logEl = this._el('serial-log');
        if (logEl) logEl.textContent = `> ${command}\nSending...`;

        const result = await this._sendCommand('run_device_command', { command, timeout_ms: 3000 });
        if (!result) {
            if (logEl) logEl.textContent += '\nError: command failed';
            return;
        }
        if (result.status === 'offline') {
            if (logEl) logEl.textContent = `> ${command}\nTerminal offline`;
            return;
        }
        if (result.status === 'timeout') {
            if (logEl) logEl.textContent = `> ${command}\nTimeout (no response)`;
            return;
        }
        const data = result.data || result;
        let output = `> ${command}  (${data.elapsed_ms || '?'}ms)\n`;
        if (data.lines?.length) {
            output += data.lines.map(l => JSON.stringify(l)).join('\n');
        } else {
            output += data.ok ? 'OK' : (data.error || 'No response data');
        }
        if (logEl) logEl.textContent = output;
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    _el(bind)            { return this.el?.querySelector(`[data-bind="${bind}"]`); }
    _setText(bind, text) { const el = this._el(bind); if (el) el.textContent = text || ''; }
    _setHTML(bind, html) { const el = this._el(bind); if (el) el.innerHTML = html || ''; }

    _onModelChange() {}

    onBeforeDestroy() {
        if (this._onDeviceStatus) {
            app.events.off('message:lock', this._onDeviceStatus);
        }
    }
}

Machine.VIEW_CLASS = TerminalView;

export default TerminalView;
